import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import config, { logger } from '../../../config';
import {
  handleApiError,
  getToken,
  getOnboardingAIData,
  updateV2OnboardingAiResult,
  patchV2OnboardingAiDisplayMetrics,
  markWalkMeThroughDone,
} from '../../../utils/api';
import { getContentReflectionOpenState, subscribeContentReflectionOpenState } from '../../../utils/contentReflectionBridge';
import { setWalkMeThroughStartHandler } from '../../../utils/walkMeThroughBridge';
import {
  calculateAllFinancialDataAI,
  normalizeOnboardingProgressKeys,
  calculatedFinancialDataToV2OnboardingAiResult,
  onboardingProgressFromV2OnboardingAiResult,
} from '../../../utils/onboardingUtils';
import {
  getFirstNameFromStorage,
  getMissingRequiredDashboardFields,
  saveOnboardingAIChatToStorage,
} from '../../../utils/helper';
import { useSuccessToast } from '../../shared/SuccessToast';
import { buildForecastHeroSubtitle } from './helpers';

function validateOnboardingProgress(progress) {
  const normalized = normalizeOnboardingProgressKeys(progress);
  if (!normalized || typeof normalized !== 'object' || Array.isArray(normalized)) {
    return { valid: false, missingFields: [] };
  }
  const missingFields = getMissingRequiredDashboardFields(normalized);
  return {
    valid: missingFields.length === 0,
    missingFields,
    progress: normalized,
  };
}

export default function useForecastPage() {
  const navigate = useNavigate();
  const abortControllerRef = useRef(null);
  const loadGenerationRef = useRef(0);
  const apiDisplayMetricsRef = useRef({ runwayDisplay: null, longevityDisplay: null });

  const [isLoading, setIsLoading] = useState(true);
  const [onboardingProgress, setOnboardingProgress] = useState(null);
  const [incomeSources, setIncomeSources] = useState(null);
  const [onboardingAiCheckupAndActionPlan, setOnboardingAiCheckupAndActionPlan] = useState(null);
  const [onboardingConfetti, setOnboardingConfetti] = useState(null);
  const [walkMeThroughAutoShow, setWalkMeThroughAutoShow] = useState(null);
  const [walkMeThroughShownCount, setWalkMeThroughShownCount] = useState(0);
  const [showWalkMeThrough, setShowWalkMeThrough] = useState(false);
  const wmtAutoTriggeredRef = useRef(false);

  const { message: successMsg, isExiting: toastExiting } = useSuccessToast();

  const redirectToLogin = useCallback(() => {
    localStorage.clear();
    navigate('/login');
  }, [navigate]);

  const forecastFirstName = getFirstNameFromStorage() || 'User';
  const forecastHeroSubtitle = useMemo(
    () => buildForecastHeroSubtitle(onboardingProgress),
    [onboardingProgress],
  );

  const saveOnboardingDataToStorage = useCallback((data) => {
    saveOnboardingAIChatToStorage(data, config);
  }, []);

  const tryApplyCachedResult = useCallback((userData) => {
    const cached = userData?.v2_onboarding_ai_result;
    if (!cached || typeof cached !== 'object' || Array.isArray(cached)) return false;
    const progress = onboardingProgressFromV2OnboardingAiResult(cached) || cached.onboardingProgress || null;
    if (!progress) return false;
    const validation = validateOnboardingProgress(progress);
    if (!validation.valid) return false;
    setOnboardingProgress(validation.progress);
    return true;
  }, []);

  const redirectNoData = useCallback((userData) => {
    if (!userData?.onboarding_ai_done) {
      navigate('/v2-onboarding-agent');
    } else {
      redirectToLogin();
    }
  }, [navigate, redirectToLogin]);

  const persistOnboardingAiResult = useCallback(async (token, financialDataResult) => {
    const result = await updateV2OnboardingAiResult(
      {
        token,
        v2OnboardingAiResult: calculatedFinancialDataToV2OnboardingAiResult(financialDataResult),
      },
      navigate
    );
    if (!result.ok) {
      logger.warn('Failed to save calculated data:', result.message || 'Unknown error');
    }
  }, [navigate]);

  const processOnboardingAgentData = useCallback(async (apiData, token) => {
    const userData = apiData.data;
    const loadedIncomeSources = userData?.income_sources;
    setIncomeSources(
      loadedIncomeSources && typeof loadedIncomeSources === 'object' && !Array.isArray(loadedIncomeSources)
        ? loadedIncomeSources
        : null
    );
    setOnboardingAiCheckupAndActionPlan(userData?.onboarding_ai_checkup_and_action_plan ?? null);
    setOnboardingConfetti(typeof userData?.onboarding_confetti === 'boolean' ? userData.onboarding_confetti : null);

    if (typeof userData?.walk_me_through_auto_show === 'boolean') {
      setWalkMeThroughAutoShow(userData.walk_me_through_auto_show);
    }
    if (typeof userData?.walk_me_through_shown_count === 'number') {
      setWalkMeThroughShownCount(userData.walk_me_through_shown_count);
    }
    const cachedResult = userData?.v2_onboarding_ai_result;
    const hasValidCache = cachedResult != null && typeof cachedResult === 'object' && !Array.isArray(cachedResult);

    if (hasValidCache && tryApplyCachedResult(userData)) return;

    const fallbackToCacheOrRedirect = (message, isError = false) => {
      (isError ? logger.error : logger.warn)(message);
      if (!tryApplyCachedResult(userData)) redirectNoData(userData);
    };

    const chatData = userData?.v2_onboarding_ai?.chat || [];
    if (chatData.length === 0) {
      fallbackToCacheOrRedirect('⚠️ No chat data available');
      return;
    }

    let financialDataResult;
    try {
      financialDataResult = await calculateAllFinancialDataAI(chatData);
    } catch (error) {
      logger.error('Error calculating financial data:', error);
      if (!tryApplyCachedResult(userData)) redirectNoData(userData);
      return;
    }

    const convertedProgress = normalizeOnboardingProgressKeys(financialDataResult?.onboardingProgress || {});
    if (Object.keys(convertedProgress).length === 0) {
      fallbackToCacheOrRedirect('❌ Converted progress is empty', true);
      return;
    }

    await persistOnboardingAiResult(token, financialDataResult);
    setOnboardingProgress(convertedProgress);
  }, [tryApplyCachedResult, redirectNoData, persistOnboardingAiResult]);

  const onAllDataLoaded = useCallback(() => setIsLoading(false), []);

  const handleDisplayMetricsReady = useCallback(async ({ runwayDisplay, longevityDisplay }) => {
    const { runwayDisplay: existingRunway, longevityDisplay: existingLongevity } = apiDisplayMetricsRef.current;
    if (existingRunway === runwayDisplay && existingLongevity === longevityDisplay) {
      return;
    }
    const result = await patchV2OnboardingAiDisplayMetrics({ runwayDisplay, longevityDisplay }, navigate);
    if (result.ok) {
      apiDisplayMetricsRef.current = { runwayDisplay, longevityDisplay };
    }
  }, [navigate]);

  useEffect(() => {
    if (isLoading || walkMeThroughAutoShow !== true) return undefined;

    let timer;
    const scheduleWalkMeThrough = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        wmtAutoTriggeredRef.current = true;
        setShowWalkMeThrough(true);
      }, 900);
    };

    if (getContentReflectionOpenState()) {
      const unsubscribe = subscribeContentReflectionOpenState((open) => {
        if (!open) scheduleWalkMeThrough();
      });
      return () => {
        unsubscribe();
        clearTimeout(timer);
      };
    }

    scheduleWalkMeThrough();
    return () => clearTimeout(timer);
  }, [isLoading, walkMeThroughAutoShow]);

  const handleWalkMeThroughDone = useCallback(async () => {
    setShowWalkMeThrough(false);
    if (wmtAutoTriggeredRef.current) {
      wmtAutoTriggeredRef.current = false;
      setWalkMeThroughAutoShow(false);
      setWalkMeThroughShownCount((count) => (count ?? 0) + 1);
      await markWalkMeThroughDone();
    }
  }, []);

  const handleWalkMeThroughButtonClick = useCallback(() => {
    wmtAutoTriggeredRef.current = false;
    setShowWalkMeThrough(true);
  }, []);

  useEffect(() => {
    setWalkMeThroughStartHandler(handleWalkMeThroughButtonClick);
    return () => setWalkMeThroughStartHandler(null);
  }, [handleWalkMeThroughButtonClick]);

  useEffect(() => {
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    const loadGeneration = ++loadGenerationRef.current;

    const loadFinancialData = async () => {
      try {
        const token = getToken();
        if (!token) {
          setIsLoading(false);
          redirectToLogin();
          return;
        }

        const apiData = await getOnboardingAIData(token, navigate, false, signal);
        if (loadGeneration !== loadGenerationRef.current || signal?.aborted) return;

        if (!apiData?.success) {
          setIsLoading(false);
          redirectToLogin();
          return;
        }

        saveOnboardingDataToStorage(apiData);

        const v2Result = apiData.data?.v2_onboarding_ai_result;
        if (v2Result && typeof v2Result === 'object' && !Array.isArray(v2Result)) {
          apiDisplayMetricsRef.current = {
            runwayDisplay: v2Result.runway_display ?? null,
            longevityDisplay: v2Result.longevity_display ?? null,
          };
        }

        if (!apiData.data?.onboarding_ai_done) {
          setIsLoading(false);
          navigate('/v2-onboarding-agent');
          return;
        }

        try {
          await processOnboardingAgentData(apiData, token);
        } catch (error) {
          if (loadGeneration !== loadGenerationRef.current || signal?.aborted) return;
          logger.error('Error processing onboarding-agent data:', error);
          setIsLoading(false);
          handleApiError(error, navigate);
        }
      } catch (error) {
        if (loadGeneration !== loadGenerationRef.current || signal?.aborted) return;
        logger.error('Error loading financial data:', error);
        setIsLoading(false);
        handleApiError(error, navigate);
      }
    };

    loadFinancialData();
    return () => {
      loadGenerationRef.current += 1;
      abortControllerRef.current?.abort();
    };
  }, [navigate, redirectToLogin, processOnboardingAgentData, saveOnboardingDataToStorage]);

  return {
    navigate,
    isLoading,
    onboardingProgress,
    incomeSources,
    onboardingAiCheckupAndActionPlan,
    onboardingConfetti,
    walkMeThroughShownCount,
    showWalkMeThrough,
    successMsg,
    toastExiting,
    forecastFirstName,
    forecastHeroSubtitle,
    onAllDataLoaded,
    handleDisplayMetricsReady,
    handleWalkMeThroughDone,
    handleWalkMeThroughButtonClick,
  };
}
